'use client';

import React, { useEffect, useRef, useState } from 'react';
import { RoundResult } from './AimTrainerApp';

interface ResultOverlayProps {
  result: RoundResult;
  reactionMs: number | null;
}

export default function ResultOverlay({ result, reactionMs }: ResultOverlayProps) {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const [displayResult, setDisplayResult] = useState<RoundResult>(null);
  const [displayMs, setDisplayMs] = useState<number | null>(null);

  // Refs to hold the active timers so we can cancel and restart them
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (fadeTimerRef.current !== null) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (result === null) return;

    // Show the new result immediately, cancelling any in-progress fade
    setDisplayResult(result);
    setDisplayMs(reactionMs);
    setVisible(true);
    setFading(false);

    // Reset the 5-second inactivity timer on every new result
    clearTimers();

    // Start fade at 5000ms, fully gone by ~5200ms
    fadeTimerRef.current = setTimeout(() => {
      setFading(true);
      hideTimerRef.current = setTimeout(() => {
        setVisible(false);
        setFading(false);
      }, 220);
    }, 5000);

    return () => {
      clearTimers();
    };
  }, [result, reactionMs]);

  if (!visible || !displayResult) return null;

  const isHit = displayResult === 'hit';
  const isEarly = displayResult === 'early';

  return (
    <div
      className="absolute pointer-events-none z-50"
      style={{
        left: '50%',
        top: '56px',
        transform: 'translateX(-50%)',
        opacity: fading ? 0 : 1,
        transition: fading ? 'opacity 0.22s ease-out' : 'opacity 0.08s ease-in',
      }}
    >
      <div
        className="flex flex-col items-center gap-0.5 px-4 py-2 rounded-lg"
        style={{
          background: isHit
            ? 'rgba(0, 255, 148, 0.10)'
            : 'rgba(255, 70, 85, 0.10)',
          border: `1px solid ${isHit ? 'rgba(0,255,148,0.35)' : 'rgba(255,70,85,0.35)'}`,
          backdropFilter: 'blur(6px)',
        }}
      >
        <span
          className="text-xl font-extrabold tracking-widest uppercase font-mono"
          style={{
            color: isHit ? 'var(--hit-green)' : 'var(--accent)',
            textShadow: isHit
              ? '0 0 14px var(--hit-green)'
              : '0 0 14px var(--accent)',
          }}
        >
          {isHit ? '✓ HIT' : isEarly ? '✗ EARLY' : '✗ MISS'}
        </span>
        {isHit && displayMs !== null && (
          <span className="text-xs font-mono text-muted-foreground tracking-widest" style={{ fontSize: '10px' }}>
            {displayMs}ms
          </span>
        )}
      </div>
    </div>
  );
}